import React from "react";
import Box from "../../../primitives/Box/Box";
import FlexColumn from "../../../components/Layout/FlexColumn/FlexColumn";
import FlexRow from "../../../components/Layout/FlexRow/FlexRow";
import { StyledImage } from "../../../components/Image/Image";
import {
  Body5MediumTypography,
  Body5RegularTypography,
  Body3MediumTypography,
  Body6MediumTypography,
  Caps2SemiboldTypography,
} from "../../../primitives/Typography/typographyVariants";
import { ActionButton } from "../../../components/ActionButton/ActionButton";
import { Icon } from "../../../components/Icon/Icon";
import { GrowthProductCardProps } from "./GrowthProductCard.types";
import styled from "@emotion/styled";
export const DiscountBadge = styled(Box)`
  background: linear-gradient(270deg, #efa396 0%, #fff 100%);
`;

export const StyledButton = styled(ActionButton)`
  margin-top: auto;
`;
const getReadableValue = (val: number | string | null | undefined): string => {
  if (val == null) return "0";
  const floatVal = parseFloat(String(val));
  if (!floatVal || isNaN(floatVal)) return "0";
  const absVal = Math.abs(floatVal);
  if (absVal >= 1000) {
    return `${(floatVal / 1000).toFixed(2)}K`;
  }
  return String(val);
};

const calculatePercentageOff = (
  price: number | string | undefined,
  mrp: number | string | undefined
): number => {
  if (!mrp || mrp === 0 || !price) return 0;
  const priceNum = typeof price === "string" ? parseFloat(price) : price;
  const mrpNum = typeof mrp === "string" ? parseFloat(mrp) : mrp;
  if (mrpNum === 0) return 0;
  const percentageOff = ((mrpNum - priceNum) / mrpNum) * 100;
  return Math.round(percentageOff);
};

export const GrowthProductCard: React.FC<GrowthProductCardProps> = ({
  growthEntity,
  showCopyButton = true,
  showClickAndOrder = true,
  onButtonClick,
  buttonText,
  className,
  dataCy,
  id,
  imageWidth = "90.75px",
  imageAspectRatio = "90.75/121.00",
}) => {
  const product = growthEntity?.product || {};
  const roundedMrp = Math.round(
    typeof product?.mrp === "string"
      ? parseFloat(product.mrp)
      : product?.mrp || 0
  );
  const roundedPrice = Math.round(
    typeof product?.price === "string"
      ? parseFloat(product.price)
      : product?.price || 0
  );
  const showMrp = roundedMrp > roundedPrice;
  const percentageOff = calculatePercentageOff(product?.price, product?.mrp);
  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onButtonClick) {
      onButtonClick(true);
    }
  };

  return (
    <FlexColumn
      p={6}
      borderRadius={8}
      border="0.5px solid"
      borderColor="border.default.default"
      gap="8px"
      className={className}
    >
      <FlexRow width="100%" gap="8px" alignItems="stretch">
        <StyledImage
          src={product?.thumbnail_url || ""}
          alt={product?.name || "product"}
          width={imageWidth}
          aspectRatio={imageAspectRatio}
          objectFit="cover"
          borderRadius="4px"
          border="0.8px solid #D7D7D7"
        />
        <FlexColumn flex="1">
          <FlexColumn gap={4}>
            <FlexColumn>
              <Caps2SemiboldTypography
                fontFamily="Lexend"
                color="text.default.secondary"
                textTransform="uppercase"
              >
                {product?.brand || ""}
              </Caps2SemiboldTypography>
              <Body5RegularTypography
                fontFamily="Lexend"
                color="text.default.secondary"
                lineClamp={2}
                textOverflow="ellipsis"
                maxWidth="100%"
              >
                {product?.name || ""}
              </Body5RegularTypography>
            </FlexColumn>
            <FlexRow gap={4}>
              {showMrp && (
                <Body5RegularTypography
                  fontFamily="Lexend"
                  color="text.default.tertiary"
                  textDecoration="line-through"
                >
                  ₹{roundedMrp}
                </Body5RegularTypography>
              )}
              <Body3MediumTypography
                fontFamily="Lexend"
                color="text.default.default"
              >
                ₹{roundedPrice}
              </Body3MediumTypography>
              {percentageOff > 0 && (
                <DiscountBadge py={2} px={4}>
                  <Body6MediumTypography
                    fontFamily="Lexend"
                    color="text.wishlink.default"
                  >
                    {percentageOff}% OFF
                  </Body6MediumTypography>
                </DiscountBadge>
              )}
            </FlexRow>
          </FlexColumn>
          {showCopyButton && (
            <StyledButton
              id={id}
              variant="primary"
              leftIcon={
                <Icon
                  name="fa-regular fa-copy"
                  size="14px"
                  height="20px"
                  width="20px"
                  color="inherit"
                />
              }
              outlined={true}
              onClick={handleButtonClick}
            >
              {buttonText}
            </StyledButton>
          )}
        </FlexColumn>
      </FlexRow>
      {showClickAndOrder && (
        <FlexRow justifyContent="space-between" alignItems="center">
          <FlexRow gap={6} width="unset">
            <Body5RegularTypography
              fontFamily="Lexend"
              color="text.default.secondary"
            >
              Clicks:
            </Body5RegularTypography>
            <Body5MediumTypography
              fontFamily="Lexend"
              color="text.default.default"
            >
              {growthEntity?.total_clicks
                ? getReadableValue(growthEntity.total_clicks)
                : 0}
            </Body5MediumTypography>
          </FlexRow>
          <FlexRow gap={6} width="unset">
            <Body5RegularTypography
              fontFamily="Lexend"
              color="text.default.secondary"
            >
              Orders:
            </Body5RegularTypography>
            <Body5MediumTypography
              fontFamily="Lexend"
              color="text.default.default"
            >
              {growthEntity?.units_sold || 0}
            </Body5MediumTypography>
          </FlexRow>
        </FlexRow>
      )}
    </FlexColumn>
  );
};
